/* eslint-disable import/prefer-default-export */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import React, { useState } from 'react';

import { Modal, Button, Form, Select } from 'antd';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { toast } from 'react-toastify';

import * as htmlToImage from 'html-to-image';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import jsZip from 'jszip';

const { Option } = Select;

export const imageDownloadNameOptions = [
  {
    name: 'Make or Manufacturer',
    value: 'manufacturer',
  },
  {
    name: 'Model or Catalog Number or ID',
    value: 'model',
  },
  {
    name: 'Description',
    value: 'description',
  },
  {
    name: 'Document Creator',
    value: 'creator',
  },
  {
    name: 'Unique Identifier',
    value: 'identifier',
  },
  {
    name: 'Measurement Equipment',
    value: 'measurement',
  },
  {
    name: 'Laboratory',
    value: 'laboratory',
  },
  {
    name: 'Report Number',
    value: 'report_number',
  },
  {
    name: 'Report Date',
    value: 'report_date',
  },
  {
    name: 'Creation Date',
    value: 'creation_date',
  },
  {
    name: 'Comments',
    value: 'comments',
  },
  {
    name: 'Spectral Quantity (e.g., W/nm)',
    value: 'spectral_quantity',
  },
];

const reportTypes = [
  { name: 'Simple', value: 'simple' },
  { name: 'Intermediate', value: 'intermediate' },
  { name: 'Full', value: 'full' },
];

export const ImageDownloadOptionsModal = ({
  imageDownloadIds,
  libraryData,
  imageDownloadName,
  imageDownloadType,
  imageDownloadModal,
  setImageDownloadIds,
  setImageDownloadStart,
  setImageDownloadName,
  setImageDownloadType,
  setImageDownloadModal,
  setTableSpinner,
}) => {
  const [imageDownloadPercentage, setImageDownloadPercentage] = useState(0);
  const [imageDownloadText, setImageDownloadText] = useState('initial');
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const clearImageDownloadData = (passed) => {
    setImageDownloadIds([]);
    setImageDownloadStart(false);
    setImageDownloadName(['model']);
    setImageDownloadType(['simple', 'intermediate', 'full']);
    setImageDownloadPercentage(0);
    setLoadingSpinner(false);
    setTableSpinner(false);
    setImageDownloadText('initial');
    if (passed) {
      toast.success(
        <div>
          <h3>Success</h3>{' '}
          <p>
            Successfully downloaded images, please check your downloads folder.
          </p>
        </div>,
        {
          toastId: 'global-toast-success',
        }
      );
    }
  };

  const progressPromiseAll = (promises, tickCallback) => {
    var len = promises.length;
    var progress = 0;

    function tick(promise) {
      promise.then(function () {
        progress++;
        tickCallback(progress, len);
      });
      return promise;
    }

    return Promise.all(promises.map(tick));
  };

  const getElementById = (imageId, reportDownloadName, timeout) => {
    return new Promise((resolve, reject) => {
      let simpleElement = null;
      let intermediateElement = null;
      let fullElement = null;

      setTimeout(() => {
        if (imageDownloadType.includes('simple')) {
          simpleElement = document.getElementById(
            `MULTIPLE_DOWNLOAD_SIMPLE_${imageId}`
          );
        }

        if (imageDownloadType.includes('intermediate')) {
          intermediateElement = document.getElementById(
            `MULTIPLE_DOWNLOAD_INTERMEDIATE_${imageId}`
          );
        }

        if (imageDownloadType.includes('full')) {
          fullElement = document.getElementById(
            `MULTIPLE_DOWNLOAD_FULL_${imageId}`
          );
        }
        resolve({
          simple: { simpleElement, reportDownloadName },
          intermediate: { intermediateElement, reportDownloadName },
          full: { fullElement, reportDownloadName },
        });
      }, timeout);
    });
  };

  const generateImageDownloadData = () => {
    return new Promise((resolve, reject) => {
      const imageDownloadDataPromises = [];
      imageDownloadIds.length !== 0 &&
        imageDownloadIds.forEach((imageId) => {
          const targetDownloadItem = libraryData.find(
            (libraryItem) => libraryItem.id === imageId
          );
          const { metadata } = targetDownloadItem;
          // Generating report name
          let reportDownloadName = 'Sample';
          if (
            imageDownloadName.length === 1 &&
            imageDownloadName.includes('model')
          ) {
            reportDownloadName = metadata.identifier.replace(/[:]/g, '-');
          } else {
            const reportDownloadNamesToAppend = imageDownloadName.map(
              (downloadName) => metadata[downloadName]
            );
            reportDownloadName = reportDownloadNamesToAppend.join('-');
          }

          // Calculate timeout
          let timeout = 3000;
          if (imageDownloadType.includes('full')) {
            timeout = imageDownloadIds.length * 3000;
          } else if (imageDownloadType.includes('intermediate')) {
            timeout = imageDownloadIds.length * 1000;
          } else {
            timeout = imageDownloadIds.length * 500;
          }

          const getElementByIdPromises = getElementById(
            imageId,
            reportDownloadName,
            (timeout += 2000)
          );

          imageDownloadDataPromises.push(getElementByIdPromises);
        });
      if (imageDownloadDataPromises.length !== 0) {
        resolve(imageDownloadDataPromises);
      } else {
        reject('Something went wrong!');
      }
    });
  };

  const getImageDownloadData = (resolvedImageDownloadData) => {
    let imageDownloadData = [];
    return new Promise((resolve, reject) => {
      resolvedImageDownloadData.forEach((images) => {
        const { simple, intermediate, full } = images;
        // Collecting Image download data
        if (imageDownloadType.includes('simple')) {
          imageDownloadData.push({
            element: simple.simpleElement,
            libraryName: simple.reportDownloadName,
            elementType: 'SIMPLE',
          });
        }

        if (imageDownloadType.includes('intermediate')) {
          imageDownloadData.push({
            element: intermediate.intermediateElement,
            libraryName: intermediate.reportDownloadName,
            elementType: 'INTERMEDIATE',
          });
        }

        if (imageDownloadType.includes('full')) {
          imageDownloadData.push({
            element: full.fullElement,
            libraryName: full.reportDownloadName,
            elementType: 'FULL',
          });
        }
      });
      if (
        imageDownloadData.length ===
        imageDownloadIds.length * imageDownloadType.length
      ) {
        resolve(imageDownloadData);
      } else {
        reject('Something went wrong!');
      }
    });
  };

  const generateImages = (imageDownloadData) => {
    return new Promise((resolve) => {
      let allPromises = [];
      imageDownloadData.forEach((imageData) => {
        // let promise = html2canvas(imageData.element, {
        //   scale: 1,
        //   backgroundColor: '#ffffff',
        // });
        let promise = htmlToImage.toPng(imageData.element, {
          backgroundColor: '#ffffff',
        });
        allPromises.push(promise);
      });
      resolve(allPromises);
    });
  };

  const generateZip = (canvases, imageDownloadData) => {
    let tm30Images = new jsZip();
    return new Promise((resolve) => {
      canvases.forEach((image, index) => {
        // const image = canvas.toDataURL('image/png');
        if (image) {
          tm30Images.file(
            `${imageDownloadData[index].libraryName}-${imageDownloadData[
              index
            ].elementType.toLowerCase()}.png`,
            image.replace(/^data:image\/(png|jpg);base64,/, ''),
            { base64: true }
          );
        }
      });
      resolve(tm30Images);
    });
  };

  const getImages = async (imageDownloadData) => {
    const imagePromises = await generateImages(imageDownloadData);
    setImageDownloadText('final');
    // const resolvedImages = await Promise.all(imagePromises);
    const resolvedCanvases = await progressPromiseAll(
      imagePromises,
      (completed, total) => {
        // setImageDownloadPercentage(Math.round((completed / total) * 100))
        setImageDownloadPercentage(Math.round((completed / total) * 100));
      }
    );
    const tm30ImagesZip = await generateZip(
      resolvedCanvases,
      imageDownloadData
    );

    tm30ImagesZip.generateAsync({ type: 'blob' }).then(function (content) {
      saveAs(content, 'Spectral-Calculator-Images.zip');
    });
    clearImageDownloadData(true);

    // Detect Safari
    // let safariAgent = navigator.userAgent?.indexOf("Safari") > -1;
    // let chromeAgent = navigator.userAgent?.indexOf("Chrome") > -1;

    // Discard Safari since it also matches Chrome
    // if (chromeAgent && safariAgent) safariAgent = false;
  };

  const handleMultipleImageDownload = async () => {
    setImageDownloadModal(false);
    setLoadingSpinner(true);
    setTableSpinner(true);

    setTimeout(() => {
      setImageDownloadStart(true);
    }, 1000);

    try {
      const imageDownloadDataPromises = await generateImageDownloadData();
      const resolvedImageDownloadData = await Promise.all(
        imageDownloadDataPromises
      );
      const imageDownloadData = await getImageDownloadData(
        resolvedImageDownloadData
      );
      getImages(imageDownloadData);
    } catch (e) {
      // See - setImageDownloadStart(true)
      setTimeout(() => {
        clearImageDownloadData(false);
      }, 1000);
      alert(e);
    }
  };

  if (loadingSpinner) {
    return (
      <div css={DownloadSpinnerStyles}>
        <CircularProgressbar
          minValue={0}
          maxValue={100}
          value={imageDownloadPercentage}
          text={`${imageDownloadPercentage}%`}
          background
          backgroundPadding={6}
          styles={buildStyles({
            backgroundColor: '#d6781e',
            textColor: '#fff',
            pathColor: '#fff',
            trailColor: 'transparent',
          })}
        />
        {imageDownloadText === 'final' ? (
          <p className='final-text'>All set! Downloading your images</p>
        ) : (
          <p className='initial-text'>
            Your download will start in a minute<span>.</span>
            <span>.</span>
            <span>.</span>
          </p>
        )}
      </div>
    );
  } else {
    return (
      <Modal
        title='Image Download Options'
        visible={imageDownloadModal}
        onCancel={() => setImageDownloadModal(false)}
        footer={[
          <Button key='cancel' onClick={() => setImageDownloadModal(false)}>
            Cancel
          </Button>,
          <Button
            key='download'
            type='primary'
            disabled={
              imageDownloadType.length === 0 || imageDownloadName.length === 0
            }
            onClick={handleMultipleImageDownload}
          >
            Download
          </Button>,
        ]}
      >
        <Form>
          <Form.Item
            label='Image download name'
            rules={[
              { required: true, message: 'At least one meta tag is required' },
            ]}
          >
            <Select
              style={{ width: '100%' }}
              mode='multiple'
              onChange={(value) => setImageDownloadName(value)}
              defaultValue={imageDownloadName}
            >
              {imageDownloadNameOptions.map((metaData) => {
                return (
                  <Option
                    key={metaData.key + metaData.value}
                    value={metaData.value}
                  >
                    {metaData.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label='Report type'
            rules={[
              {
                required: true,
                message: 'At least one report type is required',
              },
            ]}
          >
            <Select
              mode='multiple'
              allowClear
              style={{ width: '100%' }}
              placeholder='Please select'
              onChange={(values) => setImageDownloadType(values)}
              value={imageDownloadType}
            >
              {reportTypes.map((option) => (
                <Option key={option} value={option.value}>
                  {option.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <span style={{ marginBottom: '2rem' }}>
            <strong>Note:</strong> Downloads checked row images only
          </span>
        </Form>
      </Modal>
    );
  }
};

const DownloadSpinnerStyles = css`
  @keyframes slideInFromLeft {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(0);
    }
  }

  position: absolute;
  top: 33%;
  z-index: 10;
  margin: 0;
  right: 40%;
  text-align: center;
  animation: 0.5s ease-out 0s 1 slideInFromLeft;

  svg {
    width: 7rem;
  }

  .CircularProgressbar .CircularProgressbar-text {
    font-size: 22px;
  }

  p {
    font-weight: bold;
    font-size: 18px;
  }

  .final-text {
    animation: 0.5s ease-out 0s 1 slideInFromLeft;
  }

  .initial-text {
    span {
      font-size: 24px;
      animation-name: blink;
      animation-duration: 1.4s;
      animation-iteration-count: infinite;
      animation-fill-mode: both;
    }

    span:nth-child(2) {
      animation-delay: 0.2s;
    }
    span:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes blink {
    0% {
      opacity: 0.2;
    }
    20% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }
`;
